import { useRef } from 'react';
import type { FunctionComponent, KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';

import Button from '../Button';
import BareRoleButton from '../Button/BareRoleButton';
import Icon, { registerIcon } from '../Icon';
import * as timesIcon from '../Icon/icons/times.icon';
import * as minusIcon from '../Icon/icons/minus.icon';
import * as undockIcon from '../Icon/icons/undock.icon';
import { defaultThemeProp } from '../../theme';
import type { ForwardProps } from '../../types';
import Flex from '../Flex';
import { useElement, useI18n, useModalContext, useTheme } from '../../hooks';
import Text from '../Text';
import Tooltip from '../Tooltip';

import type { ModalProps } from './Modal.types';
import useModalMove from './useModalMove';
import { StyledMinimizedLabel } from './Modal.styles';

registerIcon(timesIcon, minusIcon, undockIcon);

const StyledMinimizedModal = styled(BareRoleButton)(({ theme: { base } }) => {
  return css`
    height: 3rem;
    width: fit-content;
    min-width: 30ch;
    max-width: 56ch;
    border-radius: calc(0.5 * ${base['border-radius']}) calc(0.5 * ${base['border-radius']}) 0 0;
    color: ${base.palette['foreground-color']};
    background-color: ${base.palette['primary-background']};
    box-shadow: ${base.shadow.low};
    padding: ${base.spacing};
    cursor: pointer;
    user-select: none;
    pointer-events: auto;
    transition: box-shadow ${base.animation.speed} ${base.animation.timing.ease};

    &:hover {
      box-shadow: ${base.shadow.high};
    }

    &:focus {
      outline: none;
      box-shadow: ${base.shadow.focus};
    }

    ${StyledMinimizedLabel} {
      margin-inline-end: auto;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &[data-moveable] {
      position: absolute;
    }
  `;
});

StyledMinimizedModal.defaultProps = defaultThemeProp;

const MinimizedModal: FunctionComponent<ModalProps & ForwardProps> = ({
  heading,
  onBeforeClose,
  onAfterClose,
  onRequestDismiss,
  onRequestActivate,
  onRequestMinimize,
  onRequestMaximize,
  onRequestDock,
  icon,
  ...restProps
}) => {
  const { dismissible, dockable, moveable, maximizable, dock, maximize, activate, unmount } =
    useModalContext();

  const [titleTarget, setTitleTarget] = useElement();

  const t = useI18n();

  // Note StyledMinimizedModal is a div but has the role of button
  const ref = useRef<HTMLDivElement>(null);

  const modalMoveReturnRef = useModalMove({
    enabled: !!moveable,
    moveRef: ref
  });
  const theme = useTheme();

  return (
    <Flex
      {...restProps}
      container={{ alignItems: 'center', justify: 'between', pad: [undefined, 2], gap: 1 }}
      item={{ shrink: 0 }}
      as={StyledMinimizedModal}
      aria-label={`${t('activate')} ${heading}`}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        // Prevent issues when auto focusing on close button.
        if ((e.key === 'Space' || e.key === 'Enter') && e.target === e.currentTarget)
          e.preventDefault();
      }}
      onClick={() => {
        if (modalMoveReturnRef.current?.mouseMoved) return;
        if (onRequestActivate?.() !== false && !dockable) activate();
        if (onRequestDock?.() !== false && dockable) dock();
      }}
      data-app-region
      data-moveable={moveable ? '' : undefined}
      ref={ref}
    >
      {icon && (
        <Icon
          name={icon}
          shape='square'
          size='m'
          background={theme.base.palette['app-background']}
        />
      )}
      <Text as={StyledMinimizedLabel} ref={setTitleTarget}>
        {heading}
      </Text>

      {(maximizable || dockable || dismissible) && (
        <Flex container>
          {maximizable && (
            <Button
              label={t('maximize')}
              aria-label={t('modal_maximize')}
              variant='simple'
              onClick={() => {
                if (onRequestMaximize?.() !== false) {
                  maximize();
                }
              }}
              icon
            >
              <Icon name='undock' />
            </Button>
          )}

          {dockable && (
            <Button
              label={t('dock')}
              aria-label={t('modal_dock')}
              variant='simple'
              onClick={() => {
                if (onRequestDock?.() !== false) {
                  dock();
                }
              }}
              icon
            >
              <Icon name='dock' />
            </Button>
          )}

          {dismissible && (
            <Button
              label={t('close')}
              aria-label={t('modal_close')}
              variant='simple'
              onClick={() => {
                if (onRequestDismiss?.() !== false) {
                  onBeforeClose?.();
                  unmount();
                  onAfterClose?.();
                }
              }}
              icon
            >
              <Icon name='times' />
            </Button>
          )}
        </Flex>
      )}

      {titleTarget && (
        <Tooltip target={titleTarget} smart describeTarget={false}>
          {heading}
        </Tooltip>
      )}
    </Flex>
  );
};

export default MinimizedModal;
